const imageCache = new Map();

export function preloadImage(src) {
  if (!src) return Promise.resolve(src);
  if (imageCache.has(src)) return imageCache.get(src);

  const promise = new Promise((resolve) => {
    const img = new Image();
    img.decoding = "async";
    img.onload = () => resolve(src);
    img.onerror = () => resolve(src);
    img.src = src;
  });

  imageCache.set(src, promise);
  return promise;
}

export function isImageCached(src) {
  return imageCache.has(src);
}

export function areAllCached(sources) {
  return sources.every((src) => imageCache.has(src));
}

/** Preload first `priority` images, then the rest in the background (does not wait for rest). */
export async function preloadImages(
  sources,
  { priority = 4, concurrency = 8 } = {}
) {
  const list = sources.filter(Boolean);
  if (!list.length) return;

  if (priority > 0) {
    await Promise.all(list.slice(0, priority).map(preloadImage));
  }

  const rest = list.slice(priority);
  void preloadImagesAll(rest, concurrency);
}

/** Wait until every image is in the browser cache (parallel workers). */
export async function preloadImagesAll(sources, concurrency = 12) {
  const list = sources.filter(Boolean);
  if (!list.length) return;

  let cursor = 0;
  const worker = async () => {
    while (cursor < list.length) {
      const index = cursor++;
      await preloadImage(list[index]);
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(concurrency, list.length) }, () => worker())
  );
}

export function warmImageCache(sources, limit = 6) {
  sources.slice(0, limit).forEach((src) => {
    preloadImage(src);
  });
}

/** Prefetch a short head of the gallery (hover / touch). Does not load all. */
export function prefetchGallery(sources, count = 12) {
  void preloadImages(sources.slice(0, count), {
    priority: Math.min(4, count),
    concurrency: 6,
  });
}

/** Prefetch images around the current carousel index. */
export function preloadAround(sources, index, radius = 2) {
  const start = Math.max(0, index - radius);
  const end = Math.min(sources.length, index + radius + 1);
  sources.slice(start, end).forEach((src) => {
    preloadImage(src);
  });
}
